// Word equation (OMML, <m:oMath>) -> the small inline-HTML dialect the PDF
// renderer accepts (see esc_rich in scripts/layouts.py):
//   <em> italic math letters   <sub>/<sup> scripts
//   <mf><mn>num</mn><md>den</md></mf> fraction   <mr>x</mr> overline / radicand
// Office.js' paragraph.text flattens an equation into a run of separators and
// loses every subscript/superscript, so the equation is rebuilt from OOXML.

function escapeMath(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function kids(el: Element): Element[] {
  return Array.from(el.childNodes).filter((n): n is Element => n.nodeType === Node.ELEMENT_NODE);
}

function child(el: Element, name: string): Element | undefined {
  return kids(el).find((c) => c.localName === name);
}

function attrOf(el: Element | undefined, name: string): string | undefined {
  if (!el) return undefined;
  const v = el.getAttribute(`m:${name}`) ?? el.getAttribute(name);
  return v === null ? undefined : v;
}

// A property element's <m:chr m:val="…"/>, if present.
function chrOf(pr: Element | undefined, tag: string, fallback: string): string {
  const el = pr ? child(pr, tag) : undefined;
  const v = attrOf(el, "val");
  return v === undefined ? fallback : v;
}

// Double-struck capitals that exist as single BMP letterlike characters.
const DOUBLE_STRUCK: Record<string, string> = { C: "\u2102", H: "\u210d", N: "\u2115", P: "\u2119", Q: "\u211a", R: "\u211d", Z: "\u2124" };

function run(el: Element): string {
  let text = Array.from(el.getElementsByTagName("*"))
    .filter((x) => x.localName === "t")
    .map((x) => x.textContent || "")
    .join("");
  if (!text) return "";
  const pr = child(el, "rPr");
  const sty = attrOf(pr ? child(pr, "sty") : undefined, "val");
  const scr = attrOf(pr ? child(pr, "scr") : undefined, "val");
  if (scr === "double-struck") text = text.replace(/[A-Z]/g, (c) => DOUBLE_STRUCK[c] ?? c);
  const upright = sty === "p" || sty === "b" || scr === "double-struck";
  // Italicize math letters (Word's default) BEFORE escaping so an entity such
  // as &gt; can never be split; digits/operators stay upright.
  return text
    .split(/([A-Za-z\u0370-\u03ff]+)/)
    .map((part, i) => (i % 2 === 1 && !upright ? `<em>${part}</em>` : escapeMath(part)))
    .join("");
}

export function ommlToHtml(node: Node): string {
  return convert(node).replace(/<\/em><em>/g, "");
}

function convert(node: Node): string {
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const el = node as Element;
  const name = el.localName;
  // Property / control elements carry formatting only.
  if (name.endsWith("Pr") || name === "ctrlPr") return "";

  const arg = (n: string): string => {
    const c = child(el, n);
    return c ? kids(c).map(convert).join("") : "";
  };
  const all = () => kids(el).map(convert).join("");

  switch (name) {
    case "r":
      return run(el);
    case "sSub":
      return `${arg("e")}<sub>${arg("sub")}</sub>`;
    case "sSup":
      return `${arg("e")}<sup>${arg("sup")}</sup>`;
    case "sSubSup":
      // Stacked (sup over sub at the same x), as Word draws it.
      return `${arg("e")}<mss><msu>${arg("sup")}</msu><msb>${arg("sub")}</msb></mss>`;
    case "sPre":
      return `<sub>${arg("sub")}</sub><sup>${arg("sup")}</sup>${arg("e")}`;
    case "f": {
      const type = attrOf(child(child(el, "fPr") ?? el, "type"), "val");
      if (type === "lin") return `${arg("num")}/${arg("den")}`;
      return `<mf><mn>${arg("num")}</mn><md>${arg("den")}</md></mf>`;
    }
    case "rad": {
      const deg = arg("deg");
      return `${deg ? `<sup>${deg}</sup>` : ""}√<mr>${arg("e")}</mr>`;
    }
    case "d": {
      const pr = child(el, "dPr");
      const beg = chrOf(pr, "begChr", "(");
      const end = chrOf(pr, "endChr", ")");
      const sep = chrOf(pr, "sepChr", "|");
      const es = kids(el).filter((c) => c.localName === "e");
      const parts = es.map((c) => kids(c).map(convert).join(""));
      const multiRow = es.some((c) => Array.from(c.getElementsByTagName("*")).some((x) => x.localName === "eqArr" || x.localName === "m"));
      if (multiRow) {
        // Cases / piecewise: one tall brace, rows left-aligned after it.
        const rows = parts.join(escapeMath(sep));
        return `<mc>${beg ? `<mb>${escapeMath(beg)}</mb>` : ""}<ml>${rows}</ml>${end ? `<mb>${escapeMath(end)}</mb>` : ""}</mc>`;
      }
      return `${escapeMath(beg)}${parts.join(escapeMath(sep))}${escapeMath(end)}`;
    }
    case "nary": {
      const chr = chrOf(child(el, "naryPr"), "chr", "∫");
      const sub = arg("sub");
      const sup = arg("sup");
      return `${escapeMath(chr)}${sub ? `<sub>${sub}</sub>` : ""}${sup ? `<sup>${sup}</sup>` : ""}${arg("e")}`;
    }
    case "func":
      return `${arg("fName")} ${arg("e")}`;
    case "acc": {
      const chr = chrOf(child(el, "accPr"), "chr", "̂");
      return `${arg("e")}${escapeMath(chr)}`;
    }
    case "bar":
      return `<mr>${arg("e")}</mr>`;
    case "limLow":
      return `${arg("e")}<sub>${arg("lim")}</sub>`;
    case "limUpp":
      return `${arg("e")}<sup>${arg("lim")}</sup>`;
    case "eqArr":
      return `<ml>${kids(el)
        .filter((c) => c.localName === "e")
        .map((c) => kids(c).map(convert).join(""))
        .join("<br>")}</ml>`;
    case "m":
      // Matrix: one line per row, cells separated by thin spaces.
      return kids(el)
        .filter((c) => c.localName === "mr")
        .map((row) =>
          kids(row)
            .filter((c) => c.localName === "e")
            .map((c) => kids(c).map(convert).join(""))
            .join(" ")
        )
        .join("<br>");
    default:
      // oMath, oMathPara, e, num, den, sub, sup, deg, fName, lim, box, groupChr, phant …
      return all();
  }
}
